import React, { useEffect, useState } from 'react'
import { SEE_MORE_TEXT } from '../../constants'
import { Box } from '@hub/box'
import { Grid } from '@hub/grid'
import { Stack } from '@chakra-ui/react'
import { Button } from '@hub/button'
import { H3 } from '@hub/heading'
import { LinkBox, LinkOverlay } from '@hub/link'
import { CloudinaryImage } from '../../hub-components/cloudinary-image'
import { Text } from '@hub/text'

const CardList: React.FC<
  React.PropsWithChildren<{
    heading?: string
    cards: {
      link: string
      image: string
      title: string
      classification: string
    }[]
    className?: string
    minItems: number
    buttonText?: string
  }>
> = ({ heading = '', cards, minItems = 0, buttonText = SEE_MORE_TEXT }) => {
  const [showMinItems, setShowMinItems] = useState(true)
  const [displayedCards, setDisplayedCards] = useState(cards.slice(0, minItems))

  useEffect(() => {
    setDisplayedCards(showMinItems ? cards.slice(0, minItems) : cards)
  }, [showMinItems, cards, minItems])

  return (
    <>
      {heading && <H3>{heading}</H3>}
      <Stack direction="column" spacing={4} sx={{ marginTop: 'spacing-lg' }}>
        <Grid
          templateColumns={[
            'repeat(2, 1fr)',
            'repeat(3, 1fr)',
            'repeat(4, 1fr)',
            'repeat(5, 1fr)',
          ]}
          columnGap={['spacing-sm', null, 'spacing-md']}
          rowGap={['spacing-md', null, 'spacing-md']}
        >
          {displayedCards.map(({ title, link, image, classification }) => (
            <LinkBox
              key={title}
              maxWidth={['size-16', null, 'size-17']}
              sx={{ overflow: 'hidden' }}
            >
              <LinkOverlay href={link}>
                {image ? (
                  <CloudinaryImage
                    ratio="2/3"
                    imageSetOrImage={{
                      url: String(image),
                      type: 'SQUARE',
                    }}
                    resizeMode="pad"
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 'calc(100% - 64px)',
                      backgroundColor: 'surfaceBrandSecondary',
                    }}
                  >
                    <Text
                      isBodyText
                      tone="strong"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {title}
                    </Text>
                  </Box>
                )}
                <Box
                  sx={{
                    paddingTop: 'spacing-md',
                    height: 'size-10',
                  }}
                >
                  <Text
                    isBodyText
                    tone="strong"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {title}
                  </Text>
                  <Text
                    as="p"
                    sx={{
                      color: 'textSecondary',
                    }}
                  >
                    {classification}
                  </Text>
                </Box>
              </LinkOverlay>
            </LinkBox>
          ))}
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 'spacing-lg',
          }}
        >
          {showMinItems && buttonText && (
            <Button
              sx={{
                textDecoration: 'underline',
                fontWeight: 'normal',
                textTransform: 'none',
                color: 'textPrimary',
                pointer: 'cursor',
              }}
              variant="link"
              onClick={() => setShowMinItems(false)}
            >
              {buttonText}
            </Button>
          )}
        </Box>
      </Stack>
    </>
  )
}

export default CardList
